import { type JSX, type ReactNode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * In-app replacements for the native window.confirm / prompt / alert —
 * Portal-styled, promise-based, and callable from anywhere (no provider
 * wiring). Each mounts a one-shot dialog on document.body and resolves
 * when the user acts, mirroring the native call style:
 *
 *   if (!(await confirmDialog({ message }))) return;
 *   const name = await promptDialog({ message });   // string | null
 *   await alertDialog({ message });
 *
 * Semantics match the natives: prompt resolves '' on OK-with-empty and
 * null on cancel; confirm resolves true/false; Escape / backdrop cancel.
 */

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for irreversible actions. */
  destructive?: boolean;
  /**
   * Type-to-confirm guard for high-consequence actions: the confirm button
   * stays disabled until the user types this word (case-insensitive, e.g.
   * "delete"). A click-to-copy chip makes the word a tap away.
   */
  requireText?: string;
}

export interface PromptOptions {
  title?: string;
  message: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
}

export interface AlertOptions {
  title?: string;
  message: string;
  okLabel?: string;
}

/** Mount a one-shot dialog, resolve with its result, then tear down. */
function mount<T>(render: (resolve: (value: T) => void) => JSX.Element): Promise<T> {
  return new Promise<T>((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const settle = (value: T): void => {
      resolve(value);
      // Defer teardown so we're not unmounting from inside the click handler.
      setTimeout(() => {
        root.unmount();
        host.remove();
      }, 0);
    };
    root.render(render(settle));
  });
}

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return mount<boolean>((settle) => <ConfirmDialog {...opts} onResolve={settle} />);
}

export function promptDialog(opts: PromptOptions): Promise<string | null> {
  return mount<string | null>((settle) => <PromptDialog {...opts} onResolve={settle} />);
}

export function alertDialog(opts: AlertOptions): Promise<void> {
  return mount<void>((settle) => <AlertDialog {...opts} onResolve={settle} />);
}

/* ───────────────────────── components ───────────────────────── */

function useEscape(onEscape: () => void): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onEscape();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onEscape]);
}

function Shell({
  title,
  onBackdrop,
  children,
}: {
  title?: string;
  onBackdrop: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className="app-dialog__backdrop"
      role="dialog"
      aria-modal="true"
      onClick={onBackdrop}
    >
      <div className="app-dialog" onClick={(e) => e.stopPropagation()}>
        {title !== undefined && <h3 className="app-dialog__title">{title}</h3>}
        {children}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  requireText,
  onResolve,
}: ConfirmOptions & { onResolve: (value: boolean) => void }): JSX.Element {
  useEscape(() => onResolve(false));
  const [typed, setTyped] = useState('');
  const [copied, setCopied] = useState(false);
  const gated = requireText !== undefined && requireText.length > 0;
  const matches = gated && typed.trim().toLowerCase() === requireText.toLowerCase();

  function copyWord(): void {
    if (!gated) return;
    void navigator.clipboard?.writeText(requireText).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {
        /* clipboard blocked — typing still works */
      },
    );
  }

  return (
    <Shell {...(title !== undefined ? { title } : {})} onBackdrop={() => onResolve(false)}>
      <p className="app-dialog__body">{message}</p>
      {gated && (
        <div className="app-dialog__confirm-type">
          <p className="app-dialog__type-hint">
            Type{' '}
            <button
              type="button"
              className="app-dialog__copy"
              onClick={copyWord}
              title="Click to copy"
            >
              <code>{requireText}</code>
              <span className="app-dialog__copy-icon" aria-hidden>
                {copied ? '✓' : '⧉'}
              </span>
            </button>{' '}
            to confirm.
          </p>
          <input
            className="app-dialog__input"
            type="text"
            value={typed}
            placeholder={requireText}
            onChange={(e) => setTyped(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches) onResolve(true);
            }}
            aria-label={`Type ${requireText} to confirm`}
            data-testid="app-dialog-type-input"
            autoFocus
          />
        </div>
      )}
      <div className="app-dialog__actions">
        <button type="button" className="app-dialog__cancel" onClick={() => onResolve(false)}>
          {cancelLabel}
        </button>
        <button
          type="button"
          className={`app-dialog__confirm${destructive ? ' app-dialog__confirm--destructive' : ''}`}
          onClick={() => onResolve(true)}
          disabled={gated && !matches}
          data-testid="app-dialog-confirm"
          {...(gated ? {} : { autoFocus: true })}
        >
          {confirmLabel}
        </button>
      </div>
    </Shell>
  );
}

export function PromptDialog({
  title,
  message,
  defaultValue = '',
  placeholder,
  confirmLabel = 'OK',
  onResolve,
}: PromptOptions & { onResolve: (value: string | null) => void }): JSX.Element {
  const [value, setValue] = useState(defaultValue);
  useEscape(() => onResolve(null));
  return (
    <Shell {...(title !== undefined ? { title } : {})} onBackdrop={() => onResolve(null)}>
      <p className="app-dialog__body">{message}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onResolve(value);
        }}
      >
        <input
          className="app-dialog__input"
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.currentTarget.value)}
          data-testid="app-dialog-input"
          autoFocus
        />
        <div className="app-dialog__actions">
          <button type="button" className="app-dialog__cancel" onClick={() => onResolve(null)}>
            Cancel
          </button>
          <button type="submit" className="app-dialog__confirm" data-testid="app-dialog-confirm">
            {confirmLabel}
          </button>
        </div>
      </form>
    </Shell>
  );
}

export function AlertDialog({
  title,
  message,
  okLabel = 'OK',
  onResolve,
}: AlertOptions & { onResolve: () => void }): JSX.Element {
  useEscape(() => onResolve());
  return (
    <Shell {...(title !== undefined ? { title } : {})} onBackdrop={() => onResolve()}>
      <p className="app-dialog__body">{message}</p>
      <div className="app-dialog__actions">
        <button
          type="button"
          className="app-dialog__confirm"
          onClick={() => onResolve()}
          data-testid="app-dialog-confirm"
          autoFocus
        >
          {okLabel}
        </button>
      </div>
    </Shell>
  );
}
